import { useEffect } from 'react';
import { useAuth0User } from './useAuth0User';
import { useProfile } from './useProfile';
import { CleverTap } from '@lib/clevertap';

const identifiedKey = (identity: string) => `clevertap-identified:${identity}`;

// sessionStorage access can throw (private mode, embedded webviews, restricted
// storage), so both helpers fail soft: a read failure is treated as "not yet
// identified" and a write failure is swallowed — identification still happens,
// only the once-per-session guard can't persist.
const wasIdentified = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const rememberIdentified = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Ignore — see wasIdentified.
  }
};

/**
 * Identifies the signed-in user in CleverTap exactly once per browser tab, as
 * soon as both their profileId and email are available.
 *
 * profileId is sourced from the backend profile (`profile.id`), which is keyed
 * on the same profileId the app uses — reliable regardless of when Auth0
 * assigns the `profileId` token claim (it's set asynchronously on login, so the
 * claim can lag). Email comes from the Auth0 session; identification waits for
 * both. Passes CleverTap's reserved `Identity` / `Email` / `Name` fields, and
 * guards on `sessionStorage` (per top-level browsing context, i.e. per tab) so
 * `onUserLogin` fires only once per identity per tab.
 */
export const useClevertapIdentify = (clevertapReady: boolean) => {
  const { user } = useAuth0User();
  const { profile } = useProfile();

  useEffect(() => {
    const debug = process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production';
    if (!clevertapReady) {
      if (debug) console.info('[CleverTap] identify skipped: not ready');
      return;
    }

    const identity = profile?.id;
    const email = user?.email;
    if (!identity || !email) {
      if (debug)
        console.info('[CleverTap] identify skipped', {
          hasProfileId: !!identity,
          hasEmail: !!email
        });
      return;
    }

    const key = identifiedKey(identity);
    if (wasIdentified(key)) {
      if (debug)
        console.info('[CleverTap] identify skipped: already identified');
      return;
    }

    if (debug) console.info('[CleverTap] identifying user, calling login');

    // Only persist the once-per-session guard if the SDK actually pushed —
    // otherwise a no-op (e.g. SDK not ready yet) would suppress identification
    // for the rest of the tab session.
    const identified = CleverTap.login({
      Site: {
        Identity: identity,
        Email: email,
        Name: user?.name ?? email
      }
    });

    if (identified) rememberIdentified(key);
    // Depend on the specific fields used, not whole objects, so react-query
    // reference churn on user/profile doesn't re-run the effect needlessly.
  }, [clevertapReady, profile?.id, user?.email, user?.name]);
};
